import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLogin, getLoginError } from '../hooks/useLogin'
import { loginSchema, type LoginFormData } from '../types'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Alert } from '@/components/ui/Alert'
import { useAuthStore } from '@/store/auth.store'

export function LoginForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutate: login, isPending, isError, error, isSuccess } = useLogin()
  const user = useAuthStore((s) => s.user)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) })

  useEffect(() => {
    if (isSuccess && user) {
      navigate(user.onboarding_completed_at ? '/chat' : '/onboarding', { replace: true })
    }
  }, [isSuccess, user, navigate])

  const onSubmit = (data: LoginFormData) => login(data)

  const apiError = isError ? getLoginError(error) : null

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
      {apiError && <Alert variant="error">{apiError}</Alert>}

      <Input
        label={t('auth.email')}
        type="email"
        autoComplete="email"
        placeholder={t('auth.email_placeholder')}
        error={errors.email?.message && t(errors.email.message)}
        {...register('email')}
      />

      <div className="flex flex-col gap-1">
        <Input
          label={t('auth.password')}
          type="password"
          autoComplete="current-password"
          error={errors.password?.message && t(errors.password.message)}
          {...register('password')}
        />
        <Link
          to="/auth/forgot-password"
          className="self-end text-xs text-primary-600 hover:underline"
        >
          {t('auth.forgot_password')}
        </Link>
      </div>

      <Button type="submit" isLoading={isPending} className="w-full">
        {t('auth.login_submit')}
      </Button>

      {/* L'opacité réduite ne porte que sur le texte statique : appliquée au
          <p> entier, elle diluait aussi le lien et faisait tomber son
          contraste sous le seuil AA (3,46:1 mesuré au lieu de 4,5:1). */}
      <p className="text-center text-sm text-surface-800">
        <span className="opacity-70">{t('auth.no_account')}</span>{' '}
        <Link to="/auth/register" className="font-medium text-primary-600 hover:underline">
          {t('auth.create_account')}
        </Link>
      </p>
    </form>
  )
}
